import {Button, Checkbox, FormControl, Link, Portal, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useState} from 'react'

import {blockUserPersonally} from '../../mutations/block-user-personally-mutation'

export type PersonalBlockUserDialogProps = {
  contentId: string
  contentAuthor: {login: string}
  contentUrl: string
  navigationSource?: string
  onClose: () => void
  onCompleted?: () => void
}

export const PersonalBlockUserDialog = ({
  contentId,
  contentAuthor,
  contentUrl,
  navigationSource,
  onClose,
  onCompleted,
}: PersonalBlockUserDialogProps) => {
  const [note, setNote] = useState('')
  const [closeContent, setCloseContent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleBlock = async () => {
    setIsSubmitting(true)
    await blockUserPersonally({
      input: {
        contentId,
        navigationSource,
        userLogin: contentAuthor.login,
        returnTo: contentUrl,
        note: note.length > 0 ? note : undefined,
        closeContent,
      },
      onCompleted: () => {
        onCompleted?.()
        window.location.reload()
      },
      onError: () => {
        setIsSubmitting(false)
        setErrorMessage('Unable to block user. Please try again.')
      },
    })
  }

  return (
    <Portal>
      <Dialog
        title={`Block @${contentAuthor.login}`}
        renderFooter={() => (
          <div className="p-3" style={{width: '100%'}}>
            <Button
              variant="danger"
              onClick={handleBlock}
              loading={isSubmitting}
              loadingAnnouncement="Blocking user…"
              block
            >
              Block user
            </Button>
          </div>
        )}
        width="large"
        height="auto"
        onClose={onClose}
      >
        <p>
          Blocking <strong>@{contentAuthor.login}</strong> prevents them from interacting with your repositories, such
          as opening or commenting on pull requests or issues, starring, forking, or watching. Learn more about{' '}
          <Link
            href="https://docs.github.com/articles/blocking-a-user-from-your-personal-account/"
            target="_blank"
            rel="noreferrer"
            inline
          >
            blocking a user
          </Link>
          .
        </p>
        <FormControl className="mt-2 mb-2">
          <Checkbox checked={closeContent} onChange={event => setCloseContent(event.target.checked)} />
          <FormControl.Label className="text-bold">
            Close all open issues, pull requests, and discussions opened by this user
          </FormControl.Label>
          <FormControl.Caption>Content in all repositories owned by your account will be closed.</FormControl.Caption>
        </FormControl>
        {errorMessage && (
          <p className="color-fg-danger" role="alert">
            {errorMessage}
          </p>
        )}
        <FormControl>
          <FormControl.Label>Add an optional note</FormControl.Label>
          <Textarea
            value={note}
            onChange={event => setNote(event.target.value)}
            rows={3}
            maxLength={250}
            block
            resize="vertical"
            name="note"
          />
          <FormControl.Caption>
            Maximum 250 characters. Please don&apos;t include any personal information such as legal names or email
            addresses. Markdown is supported. This note will only be visible to you.
          </FormControl.Caption>
        </FormControl>
      </Dialog>
    </Portal>
  )
}
